(ns yardwerkz.web.profile.payment-methods
  "Payment methods management with Stripe Elements.
   Uses @stripe/stripe-js imperatively (no React wrapper needed)."
  (:require
   ["@stripe/stripe-js" :refer [loadStripe]]
   [uix.core :refer [defui $ use-state use-effect use-ref use-callback]]
   [io.factorhouse.rfx.core :as rfx]
   [yardwerkz.web.config :as config]
   [yardwerkz.web.store :as store]
   [yardwerkz.web.router :as router]
   [yardwerkz.web.ui.layout :as layout]
   [yardwerkz.web.ui.components :as ui]
   [yardwerkz.web.ui.icons :as icons]))

;; =============================================================================
;; Card Brand Icons
;; =============================================================================

(defn- brand-label [brand]
  (case (some-> brand clojure.string/lower-case)
    "visa" "Visa"
    "mastercard" "Mastercard"
    "amex" "American Express"
    "discover" "Discover"
    (or brand "Card")))

;; =============================================================================
;; Card Item
;; =============================================================================

(defui card-item [{:keys [method on-delete]}]
  (let [[confirming set-confirming] (use-state false)
        [deleting set-deleting] (use-state false)
        dispatch (rfx/use-dispatch)]
    ($ ui/card {:class "p-4"}
       ($ :div {:class "flex items-center justify-between"}
          ($ :div {:class "flex items-center space-x-3"}
             ($ :div {:class "p-2 bg-gray-100 rounded-lg"}
                ($ icons/CreditCard {:size 20 :class "text-gray-600"}))
             ($ :div
                ($ :p {:class "font-medium text-gray-900"}
                   (str (brand-label (:brand method)) " •••• " (or (:last4 method) "????")))
                (when (:exp-month method)
                  ($ :p {:class "text-xs text-gray-500"}
                     (str "Expires " (:exp-month method) "/" (:exp-year method))))))
          (if confirming
            ($ :div {:class "flex items-center space-x-2"}
               ($ ui/button {:variant :danger :size :sm
                             :loading deleting
                             :on-click (fn []
                                         (set-deleting true)
                                         (dispatch [::store/delete-payment-method
                                                    {:payment-method-id (:id method)
                                                     :on-success (fn [_]
                                                                   (set-deleting false)
                                                                   (set-confirming false)
                                                                   (dispatch [::store/fetch-payment-methods])
                                                                   (ui/show-toast! :success "Card removed"))
                                                     :on-failure (fn [_]
                                                                   (set-deleting false)
                                                                   (ui/show-toast! :error "Failed to remove card"))}]))}
                  "Remove")
               ($ ui/button {:variant :ghost :size :sm
                             :on-click #(set-confirming false)}
                  "Cancel"))
            ($ :button {:class "text-gray-400 hover:text-red-500 transition-colors"
                        :on-click #(set-confirming true)}
               ($ icons/X {:size 18})))))))

;; =============================================================================
;; Add Card Modal (Stripe Elements imperative API)
;; =============================================================================

(defui add-card-modal [{:keys [visible on-close on-added]}]
  (let [dispatch (rfx/use-dispatch)
        card-ref (use-ref nil)      ;; DOM mount point
        stripe-ref (use-ref nil)    ;; Stripe instance
        elements-ref (use-ref nil)  ;; card Element
        [loading set-loading] (use-state false)
        [error set-error] (use-state nil)
        [card-ready set-card-ready] (use-state false)

        ;; Initialize Stripe and mount card element
        mount-card
        (use-callback
         (fn []
           (when (and visible @card-ref (not @stripe-ref))
             (-> (loadStripe config/STRIPE_KEY)
                 (.then (fn [^js stripe]
                          (reset! stripe-ref stripe)
                          (let [^js elems (.elements stripe)
                                ^js card (.create elems "card"
                                                  (clj->js {:style
                                                            {:base
                                                             {:fontSize "16px"
                                                              :color "#1f2937"
                                                              :fontFamily "system-ui, -apple-system, sans-serif"
                                                              "::placeholder" {:color "#9ca3af"}}}}))]
                            (reset! elements-ref card)
                            (.mount card @card-ref)
                            (.on card "ready" #(set-card-ready true))
                            (.on card "change" (fn [^js event]
                                                 (set-error (when (.-error event)
                                                              (.. event -error -message)))))))))))
         [visible])

        ;; Submit: create setup intent, confirm with Stripe
        handle-submit
        (use-callback
         (fn []
           (when (and @stripe-ref @elements-ref)
             (set-loading true)
             (set-error nil)
             (dispatch [::store/create-setup-intent
                        {:on-success
                         (fn [result]
                           (let [client-secret (or (get-in result [:data :client-secret])
                                                   (:client-secret result))]
                             (-> (.confirmCardSetup ^js @stripe-ref client-secret
                                                    #js {:payment_method #js {:card @elements-ref}})
                                 (.then (fn [^js result]
                                          (set-loading false)
                                          (if (.-error result)
                                            (set-error (.. result -error -message))
                                            (do
                                              (on-close)
                                              (when on-added (on-added))
                                              (ui/show-toast! :success "Card added successfully")))))
                                 (.catch (fn [err]
                                           (set-loading false)
                                           (set-error (.-message err)))))))
                         :on-failure
                         (fn [_]
                           (set-loading false)
                           (set-error "Failed to create setup. Please try again."))}])))
         [dispatch on-close on-added])]

    ;; Mount Stripe card element when modal opens
    (use-effect
     (fn []
       (when visible
         ;; Small delay to ensure DOM is ready
         (let [timer (js/setTimeout mount-card 100)]
           (fn []
             (js/clearTimeout timer)
             ;; Cleanup on unmount
             (when @elements-ref
               (.unmount ^js @elements-ref)
               (reset! elements-ref nil))
             (reset! stripe-ref nil)
             (set-card-ready false)
             (set-error nil)))))
     [visible mount-card])

    ($ ui/modal {:open visible :on-close on-close :title "Add Payment Method"}
       ($ :div {:class "space-y-4"}
          ;; Stripe card element mount point
          ($ :div {:class "border border-gray-300 rounded-lg p-4 min-h-[44px] bg-white focus-within:ring-2 focus-within:ring-green-500 focus-within:border-green-500"}
             ($ :div {:ref card-ref}))

          ;; Error
          (when error
            ($ :p {:class "text-sm text-red-600"} error))

          ;; Submit button
          ($ ui/button {:class "w-full"
                        :loading loading
                        :disabled (not card-ready)
                        :on-click handle-submit}
             "Add Card")

          ;; Security note
          ($ :p {:class "text-xs text-gray-400 text-center flex items-center justify-center space-x-1"}
             ($ icons/Lock {:size 12})
             ($ :span "Secured by Stripe. Card details never touch our servers."))))))

;; =============================================================================
;; Page
;; =============================================================================

(defui payment-methods-page []
  (let [dispatch (rfx/use-dispatch)
        methods (rfx/use-sub [::store/payment-methods])
        [show-add set-show-add] (use-state false)]

    (use-effect
     (fn []
       (dispatch [::store/fetch-payment-methods])
       js/undefined)
     [dispatch])

    ($ layout/page-container
       ($ :button {:class "flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
                   :on-click #(router/navigate! :profile)}
          ($ icons/ChevronLeft {:size 16})
          ($ :span "Back to Profile"))

       ($ :div {:class "flex items-center justify-between mb-6"}
          ($ :h1 {:class "text-2xl font-bold text-gray-900"} "Payment Methods")
          ($ ui/button {:size :sm :on-click #(set-show-add true)}
             ($ :span {:class "flex items-center space-x-1"}
                ($ icons/PlusCircle {:size 16})
                ($ :span "Add Card"))))

       (if (seq methods)
         ($ :div {:class "space-y-3"}
            (for [m methods]
              ($ card-item {:key (:id m)
                            :method m})))
         ($ ui/empty-state
            {:icon ($ icons/CreditCard {:size 48})
             :title "No payment methods"
             :description "Add a card to pay for services."
             :action ($ ui/button {:on-click #(set-show-add true)}
                        "Add Your First Card")}))

       ;; Add card modal
       ($ add-card-modal {:visible show-add
                          :on-close #(set-show-add false)
                          :on-added #(dispatch [::store/fetch-payment-methods])}))))
